import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CornerDownRight, Square } from 'lucide-react'
import { buildCarryOverDoc, extractOpenTodos, textOf } from '../utils/carryOverTodos'
import { formatDailyNoteTitle } from '../utils/noteDateUtils'
import type { usePersonalNoteForDate } from '../hooks/usePersonalNoteForDate'
import { Button } from '@/components/ui/button'
import { usePersonalNoteContents, usePersonalNotes } from '@/lib/db/rxdb/hooks'
import { useLocale } from '@/lib/i18n/LocaleProvider'

interface CarryOverPanelProps {
  /** yyyy-MM-dd of the note being viewed (today). */
  noteDate: string
  noteState: ReturnType<typeof usePersonalNoteForDate>
}

/**
 * Today's note offers yesterday's unfinished to-dos (design 10a, "Carried over
 * from Thu, Sep 24"). Shown only while today's note is still empty; carrying
 * them over writes them into the note as a to-do list.
 */
export function CarryOverPanel({ noteDate, noteState }: CarryOverPanelProps) {
  const { t } = useTranslation('notes')
  const { locale } = useLocale()
  const { notes } = usePersonalNotes('daily')
  const { contents } = usePersonalNoteContents()
  const [isSaving, setIsSaving] = useState(false)

  const previous = useMemo(() => {
    const earlier = notes
      .filter((n) => n.noteDate < noteDate)
      .sort((a, b) => b.noteDate.localeCompare(a.noteDate))[0]
    if (!earlier) return null
    const content = contents.find((c) => c.personalNoteId === earlier.id)
    const todos = extractOpenTodos(content?.content)
    return todos.length > 0 ? { note: earlier, todos } : null
  }, [notes, contents, noteDate])

  if (!previous || noteState.content || noteState.isLoading) return null

  const carryOver = async () => {
    setIsSaving(true)
    try {
      const id = noteState.note?.id ?? (await noteState.createNoteIfNeeded())
      if (!id) return
      const doc = JSON.stringify(buildCarryOverDoc(previous.todos))
      await noteState.createPersonalNoteContent({
        personalNoteId: id,
        content: doc,
        format: 'tiptap',
        length: doc.length,
        plainText: previous.todos.map(textOf).join('\n'),
        version: 1,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <section className="mb-6 rounded-lg border border-dashed p-3">
      <p className="mb-2 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
        <CornerDownRight className="size-3.5" />
        {t('carriedOverFrom', { date: formatDailyNoteTitle(previous.note.noteDate, locale) })}
      </p>
      <ul className="mb-3 space-y-1">
        {previous.todos.map((todo, i) => (
          <li key={i} className="flex items-center gap-2 text-[14px]">
            <Square className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{textOf(todo)}</span>
          </li>
        ))}
      </ul>
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={isSaving} onClick={() => void carryOver()}>
        {t('carryOver', { count: previous.todos.length })}
      </Button>
    </section>
  )
}
